/**
 * Advanced Similarity Engine
 * 
 * Combines multiple similarity measures with domain-specific optimizations
 * for restaurant/food service transaction and vendor matching.
 * 
 * Features:
 * - Domain-specific normalization for restaurant industry
 * - TF-IDF based corpus similarity
 * - N-gram character-level matching
 * - Phonetic similarity (Soundex)
 * - Abbreviation expansion
 */

// =============================================================================
// DOMAIN-SPECIFIC ABBREVIATIONS
// =============================================================================

/**
 * Restaurant industry abbreviation expansions
 * Maps canonical name to common variations
 */
export const ABBREVIATION_MAP: Record<string, string[]> = {
  // Major distributors
  'sysco': ['sys co', 'sysco foods', 'sysco corp', 'sysco corporation', 'sysco atlanta', 'sysco houston', 'sysco chicago', 'sysco denver'],
  'usfoods': ['us foods', 'usf', 'u.s. foods', 'us foods inc', 'usfoods inc'],
  'gordon food service': ['gfs', 'gordon fs', 'gordon food svc', 'gordon foods'],
  'performance food group': ['pfg', 'perf food', 'perf food grp', 'performance food'],
  'shamrock foods': ['shamrock', 'shamrock fds', 'shamrock foods co'],
  'ben e keith': ['ben e. keith', 'bek', 'ben e keith foods', 'ben keith'],
  'reinhart foodservice': ['reinhart', 'reinhart fs', 'reinhart food service'],
  
  // Restaurant Supply/Cash & Carry Stores
  'restaurant depot': ['rest depot', 'rest. depot', 'r depot', 'restaurantdepot', 'restaurant depot inc', 'restdepot'],
  'jetro': ['jetro cash', 'jetro cash carry', 'jetro cash & carry', 'jetro restaurant depot'],
  'smart and final': ['smart & final', 'smartfinal', 'smart final', 'smart n final', 'snf'],
  'costco': ['costco wholesale', 'costco business', 'costco business center', 'costco biz'],
  'sams club': ["sam's club", 'sams', "sam's", 'samsclub', 'sams club business'],
  'bjs wholesale': ['bjs', "bj's", "bj's wholesale", 'bjs club'],
  'cash and carry': ['cash & carry', 'cash n carry', 'cashandcarry', 'c&c'],
  'chef store': ['chefstore', 'us foods chef store', 'chef\'s store'],
  'webstaurant': ['webstaurantstore', 'webstaurant store', 'web restaurant'],
  'restaurant supply': ['rest supply', 'restaurant supply store', 'rest. supply'],
  
  // Restaurant POS Platforms
  'toast': ['toast pos', 'toast inc', 'toasttab', 'toast tab', 'tst*', 'toast deposit', 'toast payment', 'toast payout'],
  'square': ['square pos', 'sq *', 'sq*', 'squareup', 'square inc', 'square deposit', 'square payment', 'gosq.com'],
  'clover': ['clover pos', 'clover network', 'clvr', 'clover dining', 'clover payments'],
  'lightspeed': ['lightspeed pos', 'lightspeed restaurant', 'lspd', 'lightspeed hq', 'lightspeedhq'],
  'touchbistro': ['touch bistro', 'touchbistro pos', 'tb pos'],
  'revel': ['revel systems', 'revel pos', 'revelsystems'],
  'aloha': ['aloha pos', 'ncr aloha', 'aloha essentials', 'aloha cloud'],
  'ncr': ['ncr pos', 'ncr silver', 'ncr aloha', 'ncr voyix', 'ncr corporation'],
  'spoton': ['spot on', 'spoton pos', 'spot on transact', 'spoton transact'],
  'upserve': ['upserve pos', 'upserve payments', 'upserve hq'],
  'talech': ['talech pos', 'talech register'],
  'harbortouch': ['harbor touch', 'harbortouch pos', 'harbor touch pos'],
  'micros': ['oracle micros', 'micros pos', 'micros systems'],
  'aldelo': ['aldelo pos', 'aldelo express', 'aldelo pro'],
  'cake': ['cake pos', 'cake from sysco', 'sysco cake'],
  'lavu': ['lavu pos', 'lavu inc'],
  'rezku': ['rezku pos', 'rezku prime'],
  'ordermark': ['order mark', 'ordermark inc'],
  'olo': ['olo inc', 'olo online ordering', 'olo dispatch'],
  'chowly': ['chowly inc', 'chowly integration'],
  'hungerrush': ['hunger rush', 'hungerrush pos', 'revention'],
  
  // Third-Party Delivery Platforms
  'doordash': ['door dash', 'dd', 'doordash inc', 'doordash merchant', 'dd*'],
  'ubereats': ['uber eats', 'uber eat', 'ubereats merchant', 'ue*'],
  'grubhub': ['grub hub', 'gh', 'grubhub inc', 'grubhub merchant', 'seamless'],
  'postmates': ['post mates', 'postmates inc', 'postmates merchant'],
  'caviar': ['caviar delivery', 'trycaviar'],
  'gopuff': ['go puff', 'gopuff delivery'],
  'slice': ['slice pizza', 'slicelife', 'slice app'],
  'chownow': ['chow now', 'chownow inc'],
  'menufy': ['menufy inc', 'menufy online'],
  
  // Beverage distributors
  'coca cola': ['coke', 'coca-cola', 'cocacola', 'cc beverages', 'coca cola bottling'],
  'pepsi': ['pepsico', 'pepsi cola', 'pepsi-cola', 'pepsi bottling'],
  'redbull': ['red bull', 'red-bull'],
  'monster energy': ['monster', 'monster beverage'],
  'dr pepper': ['dr. pepper', 'drpepper', 'keurig dr pepper', 'kdp'],
  
  // Produce
  'produce junction': ['produce jct', 'produce jctn'],
  'baldor': ['baldor foods', 'baldor specialty foods'],
  'local harvest': ['local hrvst', 'localharvest'],
  'freshpoint': ['fresh point', 'freshpoint inc', 'sysco freshpoint'],
  
  // Dairy
  'dairy farmers': ['dairy farmers of america', 'dfa', 'dairy farmers america'],
  'land o lakes': ["land o'lakes", 'landolakes', 'land-o-lakes'],
  
  // Meat/Protein
  'tyson': ['tyson foods', 'tyson inc'],
  'smithfield': ['smithfield foods', 'smithfield inc'],
  'cargill': ['cargill meat', 'cargill foods'],
  
  // Bakery/Bread
  'bimbo bakeries': ['bimbo', 'bimbo usa', 'bimbo bakeries usa'],
  'flowers foods': ['flowers', 'flowers bakery'],
  'pepperidge farm': ['pepperidge', 'pep farm'],
  
  // Restaurant Equipment/Supplies
  'webstaurant store': ['webstaurantstore', 'webstaurant', 'web restaurant store'],
  'katom': ['katom restaurant supply', 'ka tom'],
  'central restaurant products': ['central restaurant', 'crp supplies'],
  'wasserstrom': ['wasserstrom company', 'wasserstrom restaurant'],
  'tundra restaurant supply': ['tundra supply', 'tundra restaurant'],
  'rapid packaging': ['rapidpackaging', 'rapid pack'],
  
  // Payment/Banking terms
  'ach': ['automated clearing house', 'ach payment', 'ach transfer', 'ach debit', 'ach credit'],
  'pos': ['point of sale', 'pos debit', 'pos purchase', 'pos transaction'],
  'wire': ['wire transfer', 'wire xfer', 'wire payment'],
  'check': ['check payment', 'chk', 'ck'],
  
  // Utilities
  'electric': ['elec', 'electricity', 'electric bill'],
  'natural gas': ['nat gas', 'natgas', 'gas bill'],
  'water': ['water bill', 'water utility', 'water sewer'],
  
  // Common service providers
  'comcast': ['comcast business', 'xfinity', 'comcast cable'],
  'att': ['at&t', 'at & t', 'att business'],
  'verizon': ['verizon business', 'vzw', 'verizon wireless'],
  
  // Payroll providers
  'adp': ['adp payroll', 'adp inc', 'adp llc', 'automatic data processing'],
  'gusto': ['gusto payroll', 'gusto inc', 'zenpayroll'],
  'paychex': ['paychex inc', 'paychex payroll'],
  'quickbooks payroll': ['qb payroll', 'intuit payroll'],
  'square payroll': ['sq payroll', 'square payroll inc'],
  'toast payroll': ['toast team', 'toast payroll inc'],
  
  // Food service marketplace
  'foodbuy': ['foodbuy llc', 'food buy'],
  'arrowstream': ['arrow stream', 'arrowstream inc'],
  'sourcery': ['sourcery ai', 'sourcery inc'],
  'marketman': ['market man', 'marketman inc'],
  'margin edge': ['marginedge', 'margin edge inc'],
  'restaurant365': ['r365', 'restaurant 365', 'rest365'],
  'compeat': ['compeat advantage', 'compeat restaurant'],
  
  // ==========================================================================
  // TEXAS Regional Distributors & Suppliers
  // ==========================================================================
  
  // Texas Food Distributors
  'labatt food service': ['labatt', 'labatt foods', 'labatt food svc', 'labatt san antonio', 'labatt dallas'],
  'ben e keith texas': ['ben e keith dallas', 'ben e keith fort worth', 'ben e keith houston', 'ben e keith san antonio', 'bek texas'],
  'glazier foods': ['glazier', 'glazier food company', 'glazier foods dallas'],
  'sysco texas': ['sysco houston', 'sysco dallas', 'sysco san antonio', 'sysco austin', 'sysco north texas'],
  'lone star foods': ['lone star', 'lonestar foods', 'lone star distributor'],
  'mclane foodservice texas': ['mclane texas', 'mclane dallas', 'mclane houston'],
  'hhw produce': ['hhw', 'hhw dallas', 'hhw produce company'],
  'fiesta mart': ['fiesta', 'fiesta foods', 'fiesta grocery'],
  'heb': ['h-e-b', 'h e b', 'heb grocery', 'heb foodservice', 'heb restaurant supply'],
  'central market': ['central mkt', 'heb central market'],
  
  // Texas Meat & Protein
  '44 farms': ['44farms', 'forty four farms', '44 farms beef'],
  'kreuz market': ['kreuz', 'kreuz meats'],
  'dallas meat company': ['dallas meat', 'dallas meat co'],
  'houston meat company': ['houston meat', 'houston meat co'],
  'texas beef traders': ['tx beef', 'texas beef'],
  'lone star beef': ['lonestar beef', 'ls beef'],
  'prasek family smokehouse': ['prasek', 'praseks', 'prasek smokehouse'],
  
  // Texas Produce
  'texas produce': ['tx produce', 'texas produce company'],
  'south texas produce': ['south tx produce', 'stx produce'],
  'rio star produce': ['rio star', 'riostar', 'rio star texas'],
  'texas citrus exchange': ['tx citrus', 'texas citrus'],
  
  // Texas Beverages
  'texas beer company': ['tx beer', 'texas beer co'],
  'deep ellum brewing': ['deep ellum', 'deep ellum beer'],
  'shiner': ['shiner beer', 'spoetzl', 'spoetzl brewery'],
  'topo chico': ['topochico', 'topo chico mineral'],
  
  // Texas Bakery/Tortillas
  'el milagro': ['elmilagro', 'el milagro tortillas'],
  'la banderita': ['labanderita', 'la banderita tortillas', 'ole mexican foods'],
  'mission foods': ['mission tortillas', 'mission tortilla'],
  'rudy tortillas': ['rudys tortillas', 'rudy tortilla'],
  
  // ==========================================================================
  // NEW YORK Regional Distributors & Suppliers
  // ==========================================================================
  
  // New York Food Distributors
  'baldor specialty foods': ['baldor', 'baldor foods', 'baldor nyc', 'baldor bronx'],
  'sysco metro new york': ['sysco metro ny', 'sysco new york', 'sysco ny', 'sysco metro'],
  'jersey city produce': ['jcp', 'jersey city prod'],
  'hunts point': ['hunts point market', 'hunts point produce', 'hunts point terminal'],
  'krasdale foods': ['krasdale', 'krasdale nyc'],
  'd&d produce': ['d and d produce', 'd&d prod', 'dd produce'],
  'jac creative foods': ['jac foods', 'jac creative'],
  'sid wainer': ['sid wainer and son', 'sid wainer son'],
  'fresh direct': ['freshdirect', 'fresh direct nyc', 'fd business'],
  'jet.com': ['jet delivery', 'jetcom'],
  
  // New York Meat & Protein
  'debragga': ['debragga meats', 'de bragga', 'debragga and spitler'],
  'pat lafrieda': ['lafrieda', 'la frieda', 'pat lafrieda meats', 'lafrieda meat'],
  'master purveyors': ['master purveyors inc', 'master meat'],
  'lemay and sons': ['lemay', 'lemay meats', 'lemay & sons'],
  'dicarlo foods': ['dicarlo', 'di carlo', 'dicarlo meats'],
  'first cut meats': ['first cut', 'firstcut'],
  'brooklyn provisions': ['bklyn provisions', 'brooklyn prov'],
  
  // New York Seafood
  'blue ribbon fish': ['blue ribbon seafood', 'blueribbon fish'],
  'fulton fish market': ['fulton fish', 'fulton seafood', 'new fulton fish'],
  'wild edibles': ['wildedibles', 'wild edibles seafood'],
  'browne trading': ['browne trading company', 'browne seafood'],
  'pierless fish': ['pierless', 'pier less fish'],
  'true world foods': ['true world', 'trueworld', 'true world seafood'],
  
  // New York Produce
  'coosemans new york': ['coosemans', 'coosemans ny', 'coosemans produce'],
  'sam tell produce': ['sam tell', 'samtell'],
  'j kings food service': ['j kings', 'jkings', 'j kings ny'],
  'four seasons produce ny': ['four seasons ny', '4 seasons produce'],
  
  // New York Dairy
  'milk thistle farm': ['milk thistle', 'milkthistle'],
  'ronnybrook farm': ['ronnybrook', 'ronny brook', 'ronnybrook dairy'],
  'battenkill valley': ['battenkill', 'battenkill creamery'],
  'consider bardwell': ['considerbardwell', 'bardwell farm'],
  
  // New York Bakery
  'balthazar bakery': ['balthazar', 'balthazar bread'],
  'orwashers': ['orwashers bakery', 'orwasher'],
  'grandaisy bakery': ['grandaisy', 'grand daisy'],
  'eli zabar': ['elizabar', 'eli zabar bread', 'ezs', 'elis'],
  'amy bread': ['amys bread', 'amy\'s bread'],
  'hot bread kitchen': ['hot bread', 'hotbreadkitchen'],
  
  // ==========================================================================
  // CALIFORNIA Regional Distributors & Suppliers
  // ==========================================================================
  
  // California Food Distributors
  'sysco los angeles': ['sysco la', 'sysco socal', 'sysco southern california', 'sysco riverside'],
  'sysco san francisco': ['sysco sf', 'sysco norcal', 'sysco northern california', 'sysco sacramento'],
  'sysco san diego': ['sysco sd', 'sysco san diego'],
  'smart and final california': ['smart final ca', 'smart & final la', 'smart & final sf'],
  'unified grocers': ['unified', 'unified western grocers'],
  'certified grocers': ['certified grocers ca', 'certified west'],
  'c&s wholesale': ['c and s', 'c&s grocers', 'cs wholesale'],
  'nicholas and company': ['nicholas co', 'nicholas and co'],
  
  // California Produce
  'charlie produce': ['charlie prod', 'charlie produce la'],
  'earl produce': ['earls produce', 'earl produce la'],
  'coosemans los angeles': ['coosemans la', 'coosemans california'],
  'frieda specialty produce': ['friedas', 'frieda produce', 'frieda\'s'],
  'melissa produce': ['melissas', 'melissa\'s', 'melissa produce'],
  'berkeley bowl': ['berkeley bowl produce', 'berkeleybowl'],
  'veritable vegetable': ['veritable veg', 'veritable vegetable sf'],
  'farm fresh to you': ['farmfreshtoyou', 'fftoy', 'farm fresh ca'],
  'imperfect foods': ['imperfect produce', 'imperfect foods ca'],
  'terra organics': ['terra organics produce', 'terra org'],
  
  // California Meat & Protein
  'harris ranch': ['harris ranch beef', 'harrisranch'],
  'niman ranch': ['nimanranch', 'niman', 'niman ranch meats'],
  'prather ranch': ['pratherranch', 'prather meats'],
  'marin sun farms': ['marin sun', 'marinsun'],
  'stemple creek': ['stemple creek ranch', 'stemplecreek'],
  'belcampo': ['belcampo meat', 'belcampo farms'],
  'los angeles fish company': ['la fish', 'la fish co', 'los angeles fish'],
  'santa monica seafood': ['sm seafood', 'santa monica fish', 'sms seafood'],
  
  // California Dairy
  'straus family creamery': ['straus', 'straus dairy', 'straus organic'],
  'clover sonoma': ['clover dairy', 'clover sonoma dairy', 'cloversonoma'],
  'point reyes farmstead': ['point reyes', 'pt reyes', 'point reyes cheese'],
  'cowgirl creamery': ['cowgirl', 'cowgirlcreamery'],
  'sierra nevada cheese': ['sierra nevada dairy', 'sierranevada cheese'],
  
  // California Bakery
  'la brea bakery': ['la brea', 'labrea', 'la brea bread'],
  'acme bread': ['acme bread company', 'acme bakery', 'acme sf'],
  'tartine': ['tartine bakery', 'tartine sf', 'tartine bread'],
  'bread lounge': ['breadlounge', 'bread lounge la'],
  'della fattoria': ['dellafattoria', 'della fattoria bread'],
  'josey baker bread': ['josey baker', 'joseybaker'],
  
  // California Beverages
  'lagunitas': ['lagunitas brewing', 'lagunitas beer'],
  'sierra nevada brewing': ['sierra nevada beer', 'sierra nevada ca'],
  'stone brewing': ['stone beer', 'stone brewing co'],
  'modern times': ['modern times beer', 'modern times brewing'],
  'groundwork coffee': ['groundwork', 'groundwork la'],
  'verve coffee': ['verve coffee roasters', 'verve ca'],
  'blue bottle': ['blue bottle coffee', 'bluebottle'],
  'philz coffee': ['philz', 'philzcoffee'],
  'stumptown': ['stumptown coffee', 'stumptown roasters'],
  
  // California Specialty
  'rancho gordo': ['ranchogordo', 'rancho gordo beans'],
  'bob red mill': ['bobs red mill', 'bob\'s red mill', 'bobsredmill'],
  'king arthur flour': ['king arthur', 'kingarthur'],
  'central milling': ['central milling co', 'centralmilling'],
};

/**
 * Common bank description prefixes to strip
 */
export const BANK_PREFIXES = [
  'pos debit',
  'pos purchase',
  'pos purch',
  'debit card',
  'debit card purchase',
  'ach debit',
  'ach credit',
  'ach payment',
  'wire transfer',
  'wire out',
  'wire in',
  'online transfer',
  'online payment',
  'check #',
  'check no',
  'chk #',
  'payment to',
  'payment from',
  'bill pay',
  'bill payment',
  'autopay',
  'auto pay',
  'recurring payment',
  'preauthorized',
  'pre-authorized',
  'external transfer',
  'internal transfer',
  'mobile deposit',
  'remote deposit',
];

/**
 * Restaurant industry stop words (don't add similarity weight)
 */
export const INDUSTRY_STOP_WORDS = new Set([
  'inc', 'llc', 'ltd', 'corp', 'corporation', 'co', 'company',
  'foods', 'food', 'restaurant', 'restaurants',
  'supply', 'supplies', 'supplier', 'suppliers',
  'service', 'services', 'svc', 'svcs',
  'wholesale', 'wholesaler',
  'distribution', 'dist', 'distributor', 'distributors',
  'national', 'regional', 'local',
  'group', 'grp', 'holdings',
  'enterprises', 'enterprise',
  'solutions', 'solution',
  'products', 'product',
  'usa', 'america', 'american',
  'the', 'a', 'an', 'and', 'or', 'of', 'for', 'to', 'in', 'on', 'at', 'by', 'with',
]);

// =============================================================================
// TYPES
// =============================================================================

export interface SimilarityConfig {
  /** Use TF-IDF for corpus-aware similarity */
  useTFIDF: boolean;
  /** Apply domain-specific normalization */
  domainSpecific: boolean;
  /** Minimum token length to consider */
  minTokenLength: number;
  /** N-gram size for character similarity */
  nGramSize: number;
  /** Weight for token-based similarity */
  tokenWeight: number;
  /** Weight for n-gram similarity */
  nGramWeight: number;
  /** Weight for phonetic similarity */
  phoneticWeight: number;
}

export interface SimilarityResult {
  /** Final combined similarity score (0-1) */
  score: number;
  /** Method used for final score */
  method: 'exact' | 'combined';
  /** Breakdown of individual similarity components */
  breakdown: {
    exact: number;
    token: number;
    nGram: number;
    phonetic: number;
    tfidf?: number;
  };
  /** Normalized versions of input texts */
  normalizedTexts: [string, string];
}

export const DEFAULT_SIMILARITY_CONFIG: SimilarityConfig = {
  useTFIDF: false,
  domainSpecific: true,
  minTokenLength: 2,
  nGramSize: 3,
  tokenWeight: 0.35,
  nGramWeight: 0.35,
  phoneticWeight: 0.30,
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Escape special regex characters
 */
function escapeRegex(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Soundex phonetic encoding
 * Converts a word to its phonetic code for sound-alike matching
 */
export function soundex(s: string): string {
  const word = s.toUpperCase().replace(/[^A-Z]/g, '');
  if (!word) return '';
  
  const codes: Record<string, string> = {
    B: '1', F: '1', P: '1', V: '1',
    C: '2', G: '2', J: '2', K: '2', Q: '2', S: '2', X: '2', Z: '2',
    D: '3', T: '3',
    L: '4',
    M: '5', N: '5',
    R: '6',
  };
  
  let code = word[0];
  let prevCode = codes[word[0]] || '0';
  
  for (let i = 1; i < word.length && code.length < 4; i++) {
    const char = word[i];
    const charCode = codes[char];
    if (charCode && charCode !== prevCode) {
      code += charCode;
    }
    prevCode = charCode || '0';
  }
  
  return (code + '000').slice(0, 4);
}

// =============================================================================
// NORMALIZATION
// =============================================================================

/**
 * Normalizes text for restaurant industry transactions
 * 
 * Steps:
 * 1. Lowercase and trim
 * 2. Remove bank prefixes
 * 3. Remove transaction IDs
 * 4. Remove dates and amounts
 * 5. Expand known abbreviations to canonical form
 * 6. Remove industry stop words
 */
export function normalizeForRestaurant(text: string): string {
  if (!text) return '';
  
  let normalized = text.toLowerCase().trim();
  
  // Remove bank prefixes
  for (const prefix of BANK_PREFIXES) {
    if (normalized.startsWith(prefix)) {
      normalized = normalized.slice(prefix.length).trim();
    }
    // Also check with spaces
    const prefixWithSpace = prefix + ' ';
    if (normalized.startsWith(prefixWithSpace)) {
      normalized = normalized.slice(prefixWithSpace.length).trim();
    }
  }
  
  // Remove transaction IDs (alphanumeric sequences 8+ chars that look like IDs)
  normalized = normalized.replace(/\b[a-z0-9]{10,}\b/gi, '');
  
  // Remove reference numbers with prefixes
  normalized = normalized.replace(/\b(ref|reference|conf|confirmation|txn|transaction|trace|id)[#:\s]*[a-z0-9-]+\b/gi, '');
  
  // Remove dates in various formats
  normalized = normalized.replace(/\b\d{1,2}[\/\-]\d{1,2}[\/\-]?\d{0,4}\b/g, '');
  normalized = normalized.replace(/\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s*\d{1,2},?\s*\d{0,4}\b/gi, '');
  
  // Remove amounts with dollar signs
  normalized = normalized.replace(/\$[\d,]+\.?\d*/g, '');
  
  // Remove standalone numbers (likely amounts or IDs)
  normalized = normalized.replace(/\b\d{5,}\b/g, '');
  
  // Expand known abbreviations to canonical form
  for (const [canonical, variations] of Object.entries(ABBREVIATION_MAP)) {
    for (const variation of variations) {
      const pattern = new RegExp(`\\b${escapeRegex(variation)}\\b`, 'gi');
      if (pattern.test(normalized)) {
        normalized = normalized.replace(pattern, canonical);
      }
    }
  }
  
  // Remove industry stop words
  const words = normalized.split(/\s+/);
  const filtered = words.filter(w => w.length > 1 && !INDUSTRY_STOP_WORDS.has(w));
  
  // Clean up extra whitespace
  return filtered.join(' ').replace(/\s+/g, ' ').trim();
}

/**
 * Basic normalization without domain-specific processing
 */
export function normalizeBasic(text: string): string {
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// =============================================================================
// TF-IDF SIMILARITY
// =============================================================================

/**
 * TF-IDF based similarity for transaction corpus
 * Weights terms by their distinctiveness in the corpus
 */
export class TFIDFSimilarity {
  private documentFrequency: Map<string, number> = new Map();
  private totalDocuments: number = 0;
  private isBuilt: boolean = false;
  
  constructor() {}
  
  /**
   * Check if the index has been built
   */
  get ready(): boolean {
    return this.isBuilt && this.totalDocuments > 0;
  }
  
  /**
   * Build IDF index from document corpus
   */
  buildIndex(documents: string[], domainSpecific: boolean = true): void {
    this.totalDocuments = documents.length;
    this.documentFrequency.clear();
    
    if (documents.length === 0) {
      this.isBuilt = false;
      return;
    }
    
    for (const doc of documents) {
      const terms = new Set(this.tokenize(doc, domainSpecific));
      for (const term of terms) {
        this.documentFrequency.set(
          term, 
          (this.documentFrequency.get(term) || 0) + 1
        );
      }
    }
    
    this.isBuilt = true;
  }
  
  /**
   * Tokenize document for TF-IDF
   */
  private tokenize(text: string, domainSpecific: boolean): string[] {
    const normalized = domainSpecific 
      ? normalizeForRestaurant(text) 
      : normalizeBasic(text);
    return normalized.split(/\s+/).filter(t => t.length > 2);
  }
  
  /**
   * Calculate Inverse Document Frequency
   */
  private idf(term: string): number {
    const df = this.documentFrequency.get(term) || 0;
    if (df === 0) return 0;
    return Math.log((this.totalDocuments + 1) / (df + 1)) + 1;
  }
  
  /**
   * Calculate TF-IDF vector for document
   */
  private tfidf(document: string, domainSpecific: boolean): Map<string, number> {
    const terms = this.tokenize(document, domainSpecific);
    const termFreq = new Map<string, number>();
    
    for (const term of terms) {
      termFreq.set(term, (termFreq.get(term) || 0) + 1);
    }
    
    const tfidfVector = new Map<string, number>();
    for (const [term, freq] of termFreq) {
      tfidfVector.set(term, freq * this.idf(term));
    }
    
    return tfidfVector;
  }
  
  /**
   * Calculate cosine similarity between two documents
   */
  cosineSimilarity(doc1: string, doc2: string, domainSpecific: boolean = true): number {
    if (!this.isBuilt) {
      // Fall back to basic token similarity if index not built
      return 0;
    }
    
    const v1 = this.tfidf(doc1, domainSpecific);
    const v2 = this.tfidf(doc2, domainSpecific);
    
    if (v1.size === 0 || v2.size === 0) return 0;
    
    const allTerms = new Set([...v1.keys(), ...v2.keys()]);
    
    let dotProduct = 0;
    let norm1 = 0;
    let norm2 = 0;
    
    for (const term of allTerms) {
      const val1 = v1.get(term) || 0;
      const val2 = v2.get(term) || 0;
      dotProduct += val1 * val2;
      norm1 += val1 * val1;
      norm2 += val2 * val2;
    }
    
    if (norm1 === 0 || norm2 === 0) return 0;
    return dotProduct / (Math.sqrt(norm1) * Math.sqrt(norm2));
  }
  
  /**
   * Clear the index
   */
  clear(): void {
    this.documentFrequency.clear();
    this.totalDocuments = 0;
    this.isBuilt = false;
  }
}

// =============================================================================
// N-GRAM SIMILARITY
// =============================================================================

/**
 * Calculate n-gram similarity (character-level)
 * Good for catching abbreviations and typos
 */
export function nGramSimilarity(
  str1: string, 
  str2: string, 
  n: number = 3
): number {
  if (!str1 || !str2) return 0;
  
  const s1 = str1.toLowerCase();
  const s2 = str2.toLowerCase();
  
  if (s1 === s2) return 1.0;
  
  const getNGrams = (s: string): Set<string> => {
    const ngrams = new Set<string>();
    // Pad string for edge n-grams
    const padded = ' '.repeat(n - 1) + s + ' '.repeat(n - 1);
    for (let i = 0; i < padded.length - n + 1; i++) {
      ngrams.add(padded.slice(i, i + n));
    }
    return ngrams;
  };
  
  const ngrams1 = getNGrams(s1);
  const ngrams2 = getNGrams(s2);
  
  const intersection = new Set([...ngrams1].filter(x => ngrams2.has(x)));
  const union = new Set([...ngrams1, ...ngrams2]);
  
  return union.size > 0 ? intersection.size / union.size : 0;
}

// =============================================================================
// PHONETIC SIMILARITY
// =============================================================================

/**
 * Calculate phonetic similarity using Soundex
 * Catches pronunciation-similar words
 */
export function phoneticSimilarity(str1: string, str2: string): number {
  if (!str1 || !str2) return 0;
  
  // Extract significant words (length > 2)
  const words1 = str1.toLowerCase().split(/\s+/).filter(w => w.length > 2);
  const words2 = str2.toLowerCase().split(/\s+/).filter(w => w.length > 2);
  
  if (words1.length === 0 || words2.length === 0) return 0;
  
  // Get soundex codes for each word
  const soundex1 = words1.map(soundex).filter(s => s.length > 0);
  const soundex2 = words2.map(soundex).filter(s => s.length > 0);
  
  if (soundex1.length === 0 || soundex2.length === 0) return 0;
  
  // Count matches
  let matches = 0;
  const used2 = new Set<number>();
  
  for (const s1 of soundex1) {
    for (let i = 0; i < soundex2.length; i++) {
      if (!used2.has(i) && s1 === soundex2[i]) {
        matches++;
        used2.add(i);
        break;
      }
    }
  }
  
  // Return ratio of matches to max possible
  return matches / Math.max(soundex1.length, soundex2.length);
}

// =============================================================================
// TOKEN/JACCARD SIMILARITY
// =============================================================================

/**
 * Calculate token-based Jaccard similarity
 */
export function tokenSimilarity(
  str1: string, 
  str2: string, 
  minTokenLength: number = 2
): number {
  if (!str1 || !str2) return 0;
  
  const tokens1 = new Set(
    str1.toLowerCase().split(/\s+/).filter(t => t.length >= minTokenLength)
  );
  const tokens2 = new Set(
    str2.toLowerCase().split(/\s+/).filter(t => t.length >= minTokenLength)
  );
  
  if (tokens1.size === 0 || tokens2.size === 0) return 0;
  
  const intersection = new Set([...tokens1].filter(x => tokens2.has(x)));
  const union = new Set([...tokens1, ...tokens2]);
  
  return union.size > 0 ? intersection.size / union.size : 0;
}

// =============================================================================
// COMBINED SIMILARITY
// =============================================================================

/**
 * Calculate advanced similarity score combining multiple methods
 * 
 * @param text1 - First text to compare
 * @param text2 - Second text to compare
 * @param config - Configuration options
 * @returns SimilarityResult with score and breakdown
 */
export function calculateAdvancedSimilarity(
  text1: string,
  text2: string,
  config: Partial<SimilarityConfig> = {}
): SimilarityResult {
  const finalConfig: SimilarityConfig = {
    ...DEFAULT_SIMILARITY_CONFIG,
    ...config
  };
  
  // Normalize texts
  const norm1 = finalConfig.domainSpecific 
    ? normalizeForRestaurant(text1) 
    : normalizeBasic(text1);
  const norm2 = finalConfig.domainSpecific 
    ? normalizeForRestaurant(text2) 
    : normalizeBasic(text2);
  
  // Exact match after normalization
  if (norm1 === norm2 && norm1.length > 0) {
    return {
      score: 1.0,
      method: 'exact',
      breakdown: { exact: 1, token: 1, nGram: 1, phonetic: 1 },
      normalizedTexts: [norm1, norm2]
    };
  }
  
  // Handle empty strings
  if (!norm1 || !norm2) {
    return {
      score: 0,
      method: 'combined',
      breakdown: { exact: 0, token: 0, nGram: 0, phonetic: 0 },
      normalizedTexts: [norm1, norm2]
    };
  }
  
  // Calculate individual similarities
  const tokenSim = tokenSimilarity(norm1, norm2, finalConfig.minTokenLength);
  const nGramSim = nGramSimilarity(norm1, norm2, finalConfig.nGramSize);
  const phoneticSim = phoneticSimilarity(norm1, norm2);
  
  // Weighted combination
  const score = 
    tokenSim * finalConfig.tokenWeight +
    nGramSim * finalConfig.nGramWeight +
    phoneticSim * finalConfig.phoneticWeight;
  
  return {
    score: Math.min(score, 1.0),
    method: 'combined',
    breakdown: {
      exact: 0,
      token: tokenSim,
      nGram: nGramSim,
      phonetic: phoneticSim
    },
    normalizedTexts: [norm1, norm2]
  };
}

/**
 * Calculate similarity with TF-IDF weighting (requires pre-built index)
 */
export function calculateAdvancedSimilarityWithTFIDF(
  text1: string,
  text2: string,
  tfidfIndex: TFIDFSimilarity,
  config: Partial<SimilarityConfig> = {}
): SimilarityResult {
  const baseResult = calculateAdvancedSimilarity(text1, text2, config);
  
  if (!tfidfIndex.ready) {
    return baseResult;
  }
  
  const finalConfig: SimilarityConfig = {
    ...DEFAULT_SIMILARITY_CONFIG,
    ...config
  };
  
  // Calculate TF-IDF similarity
  const tfidfSim = tfidfIndex.cosineSimilarity(text1, text2, finalConfig.domainSpecific);
  
  // Blend TF-IDF with other similarities
  // TF-IDF gets 25% weight, others get 75%
  const adjustedScore = baseResult.score * 0.75 + tfidfSim * 0.25;
  
  return {
    ...baseResult,
    score: Math.min(adjustedScore, 1.0),
    breakdown: {
      ...baseResult.breakdown,
      tfidf: tfidfSim
    }
  };
}

// =============================================================================
// BATCH SIMILARITY
// =============================================================================

/**
 * Find most similar items from a corpus
 */
export function findMostSimilar(
  query: string,
  corpus: string[],
  topK: number = 5,
  minSimilarity: number = 0.5,
  config: Partial<SimilarityConfig> = {}
): Array<{ text: string; index: number; similarity: SimilarityResult }> {
  const results: Array<{ text: string; index: number; similarity: SimilarityResult }> = [];
  
  for (let i = 0; i < corpus.length; i++) {
    const similarity = calculateAdvancedSimilarity(query, corpus[i], config);
    if (similarity.score >= minSimilarity) {
      results.push({
        text: corpus[i],
        index: i,
        similarity
      });
    }
  }
  
  // Sort by similarity score descending
  results.sort((a, b) => b.similarity.score - a.similarity.score);
  
  return results.slice(0, topK);
}

// =============================================================================
// EXPORTS
// =============================================================================

// Create a singleton TF-IDF instance for reuse
export const globalTFIDFIndex = new TFIDFSimilarity();
